
import Vue from 'vue';
import rootConfig from '@root/config/root.config';
import { Data as ArticleData } from '@root/modules/article/types/article';
import { Config } from '@root/modules/channel/types/channel';
import BookmarkButton from '@root/common/components/base/BookmarkButton.vue';
import { TranslateResult } from 'vue-i18n';

interface Props {
  article: ArticleData;
  sticky: boolean;
}

interface Computed {
  facebookUrl: string;
  linkedInUrl: string;
  messengerUrl: string;
  infoSettings: Config['page']['article']['component']['info'];
}

interface Data {
  popup: {
    width: number;
    height: number;
  };
  share: {
    title: string;
    url: string;
  };
  facebook: number;
}

interface Methods {
  copyLink: (string: string) => void;
  openPrint: () => void;
  openPopup: (url: string, provider?: string) => void;
  openPlayer: () => void;
  showLayer: (text: TranslateResult | string) => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    BookmarkButton,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    sticky: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      popup: {
        width: 720,
        height: 550,
      },
      share: {
        title: this.article.content.title.text,
        url: '',
      },
      facebook: this.article.statistics.shares || 0,
    };
  },
  computed: {
    facebookUrl() {
      return `${rootConfig.social_share.facebook}?u=${encodeURI(this.share.url)}&t=${encodeURI(this.share.title)}`;
    },
    linkedInUrl() {
      return `${rootConfig.social_share.linkedin}?mini=true&url=${encodeURI(this.share.url)}`;
    },
    messengerUrl() {
      return `${rootConfig.social_share.messenger}?app_id=1476286675946138&link=${encodeURI(this.share.url)}&redirect_uri=${encodeURI(this.share.url)}`;
    },
    infoSettings() {
      return this.$channelConfig('page').article.component.info;
    },
  },
  mounted() {
    const { baseUrl } = this.$channelConfig('meta').base;
    this.share.url = `${baseUrl}/a/${this.article.id}`;
  },
  methods: {
    copyLink(string) {
      let textarea;
      let result;
      this.$store.commit('analytics/setClickEvent', {
        googleAnalytics: { eventName: 'ANALYTICS_SHARE_CLICK', eventData: { type: 'share', data: 'Copy link' } },
      });
      try {
        textarea = document.createElement('textarea');
        textarea.setAttribute('readonly', 'true');
        textarea.setAttribute('contenteditable', 'true');
        textarea.style.position = 'fixed'; // prevent scroll from jumping to the bottom when focus is set.
        textarea.value = string;

        document.body.appendChild(textarea);

        textarea.select();

        const range = document.createRange();
        range.selectNodeContents(textarea);

        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);

        textarea.setSelectionRange(0, textarea.value.length);
        result = document.execCommand('copy');
        this.showLayer(this.$t(`info_layer.url_copied`));
      } catch (err) {
        result = null;
      } finally {
        if (textarea) {
          document.body.removeChild(textarea);
        }
      }

      // manual copy fallback using prompt
      if (!result) {
        const isMac = navigator.platform.toUpperCase().includes('MAC');
        const copyHotkey = isMac ? '⌘C' : 'CTRL+C';
        result = prompt(`Press ${copyHotkey}`, string); // eslint-disable-line no-alert
        if (!result) {
          return false;
        }
      }
      return true;
    },
    openPopup(url, provider) {
      if (window && window.open) {
        this.$store.commit('analytics/setClickEvent', {
          googleAnalytics: { eventName: 'ANALYTICS_SHARE_CLICK', eventData: { type: 'share', data: provider } },
        });
        window.open(
          url,
          'popup_window',
          `width=${this.popup.width},height=${this.popup.height},directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no`
        );
      }
    },
    openPlayer() {
      const url = `${rootConfig.player_url}/${this.$channelConfig('settings').id}/article/${this.article.id}`;
      this.openPopup(url);
      this.$store.commit('analytics/setClickEvent', {
        googleAnalytics: {
          eventName: 'ANALYTICS_OPEN_PLAYER_CLICK',
          eventData: { type: 'open', data: { articleId: this.article.id } },
        },
      });
    },
    openPrint() {
      if (window && window.print) {
        this.$store.commit('analytics/setClickEvent', {
          googleAnalytics: { eventName: 'ANALYTICS_SHARE_CLICK', eventData: { type: 'share', data: 'Print' } },
        });
        window.print();
      }
    },
    showLayer(text) {
      if (!text) {
        return false;
      }
      const $infoLayer = document.createElement('div');
      $infoLayer.innerHTML = '<span>' + text + '</span>';
      $infoLayer.classList.add('C-info-layer');
      $infoLayer.style.display = 'block';
      document.body.appendChild($infoLayer);
      setTimeout(function () {
        $infoLayer.style.display = 'none';
        $infoLayer.parentNode?.removeChild($infoLayer);
      }, 2000);
    },
  },
});
